import { Box, Flex, Heading, Stack, Text } from '@chakra-ui/react';
import { useLingui } from '@lingui/react/macro';

import { Image } from '#features/ui';
import type { FacetValue, GameFacets as GameFacetsData } from '#features/landing/utils/gameFacets';

type GameFacetsProps = {
  gameName: string;
  facets: GameFacetsData;
  accentColor: string;
};

const strip = (value: string | null | undefined): string =>
  (value ?? '').replace(/^[^\p{L}\p{N}]+/u, '').trim();

const CheckSvg = ({ color }: { color: string }) => (
  <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="3.5" strokeLinecap="round" strokeLinejoin="round">
    <path d="M20 6L9 17l-5-5" />
  </svg>
);

const Tag = ({ children }: { children: React.ReactNode }) => (
  <Text
    as="span"
    fontSize="2xs"
    fontWeight="600"
    color="fg.emphasized"
    px={1.5}
    py={0.5}
    borderRadius="md"
    bg="whiteAlpha.100"
    border="1px solid"
    borderColor="whiteAlpha.200"
    whiteSpace="nowrap"
  >
    {children}
  </Text>
);

/** One matching dimension: label + a few real example values from admin as proof. */
type Criterion = { label: string; count: number; examples: FacetValue[] };

const CriterionRow = ({ criterion, accentColor }: { criterion: Criterion; accentColor: string }) => {
  const { t } = useLingui();

  return (
  <Flex gap={3} alignItems="flex-start">
    <Flex
      w="20px"
      h="20px"
      mt="1px"
      borderRadius="md"
      alignItems="center"
      justifyContent="center"
      flexShrink={0}
      bg={`${accentColor}22`}
      border="1px solid"
      borderColor={`${accentColor}55`}
    >
      <CheckSvg color={accentColor} />
    </Flex>
    <Box>
      <Flex alignItems="baseline" gap={2} flexWrap="wrap">
        <Text fontSize="sm" fontWeight="700" color="fg.emphasized">
          {criterion.label}
        </Text>
        <Text fontSize="xs" color="fg.subtle">
          {t`${criterion.count} options`}
        </Text>
      </Flex>
      <Flex gap={1.5} mt={1.5} flexWrap="wrap">
        {criterion.examples.map((v) => (
          <Tag key={v.name}>{strip(v.name)}</Tag>
        ))}
      </Flex>
    </Box>
  </Flex>
  );
};

/**
 * Frames the game's real admin facets (version / rank / legends / role / mode / region) as what the
 * matching runs on - not a catalog, but proof that squads are built for *this* game. A sample
 * squadmate card (assembled from the game's own data) makes the "matched for you" idea concrete.
 * Adapts to whatever each game actually has configured.
 */
export const GameFacets = ({ gameName, facets, accentColor }: GameFacetsProps) => {
  const { t } = useLingui();

  // Admin `charactersLabel` is one of a fixed English set - localize via literal-string macros.
  const charactersLabel = ((label: string): string => {
    switch (label) {
      case 'Agents':
        return t`Agents`;
      case 'Legends':
        return t`Legends`;
      case 'Heroes':
        return t`Heroes`;
      case 'Operators':
        return t`Operators`;
      default:
        return t`Characters`;
    }
  })(facets.charactersLabel);

  // Build the list of matching criteria from whatever the game has.
  const criteria: Criterion[] = [];
  // The edition goes first: players of different editions can't play together at all.
  if (facets.versions.length > 0) {
    criteria.push({
      label: t`Game version`,
      count: facets.versions.length,
      examples: facets.versions.slice(0, 3),
    });
  }
  if (facets.ranks.length > 0) {
    criteria.push({ label: t`Rank`, count: facets.ranks.length, examples: facets.ranks.slice(0, 3) });
  }
  if (facets.characters.length > 0) {
    criteria.push({
      label: charactersLabel,
      count: facets.characters.length,
      examples: facets.characters.slice(0, 3),
    });
  }
  if (facets.roles.length > 0) {
    criteria.push({ label: t`Role`, count: facets.roles.length, examples: facets.roles.slice(0, 3) });
  }
  if (facets.gameModes.length > 0) {
    criteria.push({ label: t`Game mode`, count: facets.gameModes.length, examples: facets.gameModes.slice(0, 3) });
  }
  if (facets.regions.length > 0) {
    criteria.push({ label: t`Region`, count: facets.regions.length, examples: facets.regions.slice(0, 3) });
  }

  if (criteria.length === 0) {
    return null;
  }

  // Sample squadmate assembled from the game's own facets (the "matched for you" proof).
  const sampleCharacter = facets.characters.find((c) => c.image) ?? facets.characters[0];
  const sampleRank = facets.ranks[facets.ranks.length - 1]?.name;
  const sampleRole = strip(facets.roles[0]?.name) || (facets.characters[0] ? strip(facets.characters[0].name) : '');
  const sampleRegion = strip(facets.regions[0]?.name);
  const sampleTags = [sampleRank, sampleRole, sampleRegion].map(strip).filter(Boolean).slice(0, 3);

  return (
    <Stack gap={{ base: 8, md: 10 }}>
      {/* Header follows the same recipe as every other section (centered, purple eyebrow,
          uppercase 900 heading) so the game-specific block reads as part of the system,
          not a different template. The game accent stays in the facet checks + card glow. */}
      <Stack gap={3} maxW="720px" mx="auto" textAlign="center" alignItems="center">
        <Text fontSize="xs" fontWeight="700" color="purple.300" textTransform="uppercase" letterSpacing="0.15em">
          {t`Made for ${gameName}`}
        </Text>
        <Heading
          as="h2"
          fontSize={{ base: '3xl', md: '5xl' }}
          fontWeight="900"
          letterSpacing="-0.02em"
          lineHeight="1.05"
          textTransform="uppercase"
          css={{ textWrap: 'balance' }}
        >
          {t`Matched by what actually wins games in ${gameName}`}
        </Heading>
        <Text fontSize={{ base: 'md', md: 'lg' }} color="fg.muted" lineHeight="1.7">
          {t`Not "online gaming" in general. We pair you on the rank ladder, roster and region of ${gameName} - so the squad you get actually fits how you play.`}
        </Text>
      </Stack>

      <Flex
        direction={{ base: 'column', md: 'row' }}
        gap={{ base: 8, md: 12 }}
        alignItems="flex-start"
        justifyContent="center"
        maxW="920px"
        mx="auto"
        w="100%"
      >
        {/* Sample squadmate - the "matched for you" idea made concrete from real game data */}
        <Box
          w={{ base: '100%', md: '340px' }}
          flexShrink={0}
          p={5}
          borderRadius="2xl"
          border="1px solid"
          borderColor="whiteAlpha.200"
          background="linear-gradient(165deg, hsla(228,11.6%,15%,0.9) 0%, hsla(228,11.6%,10%,0.9) 100%)"
          style={{ boxShadow: `0 20px 50px -20px rgba(0,0,0,0.7), 0 0 30px -14px ${accentColor}66` }}
        >
          <Flex alignItems="center" gap={2} mb={4}>
            <Flex
              w="18px"
              h="18px"
              borderRadius="full"
              alignItems="center"
              justifyContent="center"
              bg="#22c55e"
              flexShrink={0}
              style={{ boxShadow: '0 0 8px rgba(34,197,94,0.6)' }}
            >
              <CheckSvg color="white" />
            </Flex>
            <Text fontSize="xs" fontWeight="700" color="fg.emphasized" textTransform="uppercase" letterSpacing="0.06em">
              {t`Great fit`}
            </Text>
            <Text fontSize="xs" color="fg.subtle" ml="auto">
              {t`97% match`}
            </Text>
          </Flex>

          <Flex gap={3} alignItems="center">
            <Box
              w="56px"
              h="56px"
              borderRadius="xl"
              overflow="hidden"
              flexShrink={0}
              border="1px solid"
              borderColor="whiteAlpha.200"
              bg={`${accentColor}22`}
            >
              {sampleCharacter?.image && (
                <Image
                  src={sampleCharacter.image}
                  sizes="56px"
                  alt={sampleCharacter.name}
                  loading="lazy"
                  style={{ objectFit: 'cover', width: '100%', height: '100%' }}
                />
              )}
            </Box>
            <Box>
              <Text fontSize="md" fontWeight="700" color="fg.emphasized" lineHeight="1.1">
                {t`Squadmate ready`}
              </Text>
              {sampleCharacter && (
                <Text fontSize="sm" color="fg.muted">
                  {t`Mains ${strip(sampleCharacter.name)}`}
                </Text>
              )}
            </Box>
          </Flex>

          {sampleTags.length > 0 && (
            <Flex gap={1.5} mt={4} flexWrap="wrap">
              {sampleTags.map((tag) => (
                <Tag key={tag}>{tag}</Tag>
              ))}
            </Flex>
          )}
        </Box>

        {/* What the matching runs on - real per-game criteria */}
        <Stack gap={5} flex="1" pt={{ base: 0, md: 1 }}>
          <Text fontSize="xs" fontWeight="700" color="fg.subtle" textTransform="uppercase" letterSpacing="0.08em">
            {t`What we match you on in ${gameName}`}
          </Text>
          <Stack gap={4}>
            {criteria.map((criterion) => (
              <CriterionRow key={criterion.label} criterion={criterion} accentColor={accentColor} />
            ))}
          </Stack>
        </Stack>
      </Flex>
    </Stack>
  );
};
